import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createProviderConfig,
  deleteProviderConfig,
  listProviderConfigs,
  listProviderTypes,
  updateProviderConfig,
} from '@/api/endpoints';
import type { ProviderConfig } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmTypedDelete } from '@/components/ConfirmTypedDelete';
import { ProviderConfigDialog, type ProviderConfigForm } from './ProviderConfigDialog';
import { SecretBadge } from './SecretBadge';

export function AdminProviderConfigsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const configs = useQuery<ProviderConfig[]>({
    queryKey: ['admin', 'provider-configs'],
    queryFn: listProviderConfigs,
  });
  const types = useQuery({
    queryKey: ['admin', 'provider-types'],
    queryFn: listProviderTypes,
  });

  const [editing, setEditing] = useState<ProviderConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProviderConfig | null>(null);

  const create = useMutation({
    mutationFn: (values: ProviderConfigForm) =>
      createProviderConfig({
        provider_type: values.provider_type,
        display_name: values.display_name,
        client_id: values.client_id,
        client_secret: values.client_secret,
        enabled: values.enabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'provider-configs'] });
      enqueueSnackbar('Provider config created.', { variant: 'success' });
      setCreating(false);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: number; values: ProviderConfigForm }) =>
      updateProviderConfig(id, {
        display_name: values.display_name,
        client_id: values.client_id,
        // Empty client_secret means "leave alone" — back end honours this.
        client_secret: values.client_secret || undefined,
        enabled: values.enabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'provider-configs'] });
      enqueueSnackbar('Provider config updated.', { variant: 'success' });
      setEditing(null);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteProviderConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'provider-configs'] });
      enqueueSnackbar('Provider config deleted.', { variant: 'success' });
      setPendingDelete(null);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  return (
    <Container maxWidth="lg" disableGutters>
      <PageHeader
        title="Provider configs"
        subtitle="OAuth client credentials. Secrets are write-only — they are never returned by the API."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            disabled={(types.data?.length ?? 0) === 0}
          >
            New provider
          </Button>
        }
      />

      {configs.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {configs.isSuccess && configs.data.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              No providers configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a provider config to let users connect calendar accounts.
            </Typography>
            <Button variant="contained" onClick={() => setCreating(true)}>
              Add provider
            </Button>
          </CardContent>
        </Card>
      )}

      {configs.isSuccess && configs.data.length > 0 && (
        <TableContainer component={Card} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Secret</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.data.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.display_name}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={c.provider_type} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, maxWidth: 240 }}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.client_id}</Box>
                  </TableCell>
                  <TableCell>
                    <SecretBadge hasSecret={c.has_client_secret} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={c.enabled ? 'success' : 'default'}
                      label={c.enabled ? 'Yes' : 'No'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="Edit" onClick={() => setEditing(c)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton aria-label="Delete" onClick={() => setPendingDelete(c)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ProviderConfigDialog
        mode="create"
        open={creating}
        types={types.data ?? []}
        onClose={() => setCreating(false)}
        onSubmit={(values) => create.mutate(values)}
        pending={create.isPending}
      />

      <ProviderConfigDialog
        mode="edit"
        open={editing !== null}
        initial={editing}
        types={types.data ?? []}
        onClose={() => setEditing(null)}
        onSubmit={(values) => editing && update.mutate({ id: editing.id, values })}
        pending={update.isPending}
      />

      <ConfirmTypedDelete
        open={pendingDelete !== null}
        title="Delete provider config"
        body={
          pendingDelete
            ? `Deleting "${pendingDelete.display_name}" will revoke all user connections that used it. Users will need to reconnect with a replacement provider config.`
            : undefined
        }
        confirmText={pendingDelete?.display_name ?? ''}
        pending={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
